import React, { useState, useContext } from 'react';
import FormControl from 'components/Core/FormControl';
import { STIcon } from 'components/Brand/Icons/STIcon';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import postGoogleSheets from 'services/ccpaService';
import Select from 'components/Core/Select';
import { stateList } from 'components/Form/utils/generalFormData';
import TextField from 'components/Core/TextField';
import Button from 'components/Core/Button';
import FormLabelWithField from 'components/Core/FormLabelWithField';
import SweetScrollContext from 'utils/SweetScrollContext';
import { getFormValid, fixedSubmitForm } from 'utils/formUtils';
import { CCPAFormContainer, CCPAFormWrapper } from './styles';
import { createJiraTicket } from '../../../services/createJiraTicket';

const CCPAForm = ({ width, height, ...otherProps }) => {
  const {
    values,
    setFieldValue,
    setFieldTouched,
    submitCount,
    submitForm,
    validateForm,
    resetForm,
    errors,
    touched,
  } = otherProps;
  const [submitStatus, setSubmitStatus] = useState(0); // 0: Normal, 1: Submitting, 2: Submit Success
  const formValid = getFormValid({ errors, submitCount });
  const { scrollToAnchor } = useContext(SweetScrollContext);

  const typeList = [
    {
      label: 'Request to know/access personal information',
      value: 'Request to know/access personal information',
    },
    {
      label: 'Request to correct personal information',
      value: 'Request to correct personal information',
    },
    {
      label: 'Request to delete personal information',
      value: 'Request to delete personal information',
    },
    {
      label: 'Request to opt out of sales and sharing (targeting) **',
      value: 'Request to opt out of sales and sharing (targeting)',
    },
  ];

  const submit = async () => {
    const { comment, firstName, lastName, email, phone, state, type } = values || {};

    const payload = {
      fields: {
        project: {
          id: '12735',
        },
        summary: `Data Privacy Request for - ${firstName} ${lastName}`,
        description: {
          version: 1,
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: comment,
                },
              ],
            },
          ],
        },
        issuetype: {
          name: 'Privacy Request',
        },
        customfield_15768: firstName,
        customfield_15769: lastName,
        customfield_12808: email,
        customfield_13233: phone,
        customfield_16032: state,
        customfield_16033: type,
      },
    };

    setSubmitStatus(1);

    fixedSubmitForm({ submitForm, validateForm })
      .then(() => {
        createJiraTicket(payload);
        postGoogleSheets(values)
          .then(() => {
            resetForm();
            setSubmitStatus(2);
          })
          .catch(() => {
            setSubmitStatus(0);
          });
      })
      .catch(() => {
        setSubmitStatus(0);
        scrollToAnchor('#ccpa-form');
      });
  };

  return (
    <CCPAFormWrapper>
      <CCPAFormContainer id="ccpa-form" width={width} height={height} {...otherProps}>
        <div className="ccpa-form--header">
          <p className="ccpa-form--description">* Required information</p>
        </div>

        <div className="ccpa-form--form">
          <FormLabelWithField
            formControlProps={{ className: 'ccpa-form--form-item' }}
            label="First Name"
            isRequired
          >
            <TextField
              id="firstName"
              type="text"
              nameType="First Name"
              placeholderText=""
              values={values}
              formValid={formValid}
              formErrors={errors}
              handleChange={(id, value) => setFieldValue(id, value)}
              handleBlur={(id) => setFieldTouched(id)}
            />
          </FormLabelWithField>

          <FormLabelWithField
            formControlProps={{ className: 'ccpa-form--form-item' }}
            label="Last Name"
            isRequired
          >
            <TextField
              id="lastName"
              type="text"
              nameType="Last Name"
              placeholderText=""
              values={values}
              formValid={formValid}
              formErrors={errors}
              handleChange={(id, value) => setFieldValue(id, value)}
              handleBlur={(id) => setFieldTouched(id)}
            />
          </FormLabelWithField>

          <FormLabelWithField
            formControlProps={{ className: 'ccpa-form--form-item' }}
            label="Email"
            isRequired
          >
            <TextField
              id="email"
              type="text"
              nameType="Email"
              placeholderText=""
              values={values}
              formValid={formValid}
              formErrors={errors}
              handleChange={(id, value) => setFieldValue(id, value)}
              handleBlur={(id) => setFieldTouched(id)}
            />
          </FormLabelWithField>

          <FormLabelWithField
            formControlProps={{ className: 'ccpa-form--form-item' }}
            label="Phone"
            isRequired
          >
            <TextField
              id="phone"
              type="telephone"
              format="(###) ###-####"
              nameType="Phone"
              placeholderText=""
              values={values}
              formValid={formValid}
              formErrors={errors}
              handleChange={(id, value) => setFieldValue(id, value)}
              handleBlur={(id) => setFieldTouched(id)}
            />
          </FormLabelWithField>

          <FormLabelWithField
            formControlProps={{ className: 'ccpa-form--form-item' }}
            label="State of Residence"
            isRequired
          >
            <Select
              id="state"
              placeholder="Select State"
              options={stateList}
              values={values}
              formValid={formValid}
              handleChange={(id, value) => setFieldValue(id, value)}
            />
          </FormLabelWithField>
          <FormLabelWithField
            formControlProps={{ className: 'ccpa-form--form-item' }}
            label="Request Type"
            isRequired
          >
            <Select
              id="type"
              placeholder="Select Request Type"
              options={typeList}
              values={values}
              formValid={formValid}
              handleChange={(id, value) => setFieldValue(id, value)}
            />
          </FormLabelWithField>
          <FormLabelWithField
            formControlProps={{ className: 'ccpa-form--form-item' }}
            label="Please describe your request"
            isRequired
          >
            <TextField
              id="comment"
              style={{
                borderWidth: 1,
                borderColor: formValid.comment === -1 ? '#f00' : 'var(--neutral-3)',
              }}
              value={values.comment}
              onChange={(e) => setFieldValue('comment', e.target.value)}
              onBlur={() => setFieldTouched('comment')}
              multiline
              rows={7}
              maxRows={7}
            />
          </FormLabelWithField>

          <p className={`ccpa-form--form-success${submitStatus === 2 ? ' success-visible' : ''}`}>
            ServiceTitan has received and is currently processing your request submitted via our
            Consumer Privacy Requests webform. We will send communications and responses related to
            your request via email to this email address.
          </p>

          <FormControl>
            <Button
              htmlType="submit"
              className="ccpa-form--submit-button"
              type="primary"
              shadow={false}
              onClick={submit}
              disabled={submitStatus === 1}
            >
              Submit
              {submitStatus === 1 && <STIcon type="icon-loading" style={{ marginLeft: '5px' }} />}
            </Button>
          </FormControl>
        </div>
      </CCPAFormContainer>
    </CCPAFormWrapper>
  );
};

const initValues = {
  type: 'Request to opt out of sales and sharing (targeting)',
  phone: '',
};

const vSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('First Name is required'),
  lastName: Yup.string().trim().required('Last Name is required'),
  email: Yup.string().email().required('Email is required'),
  phone: Yup.string().required('Phone is required').min(14, 'Phone must be 10 numbers').nullable(),
  state: Yup.string().required('State is required'),
  type: Yup.string().required('Request Type is required'),
  comment: Yup.string().required('Comment is required'),
});

export default withFormik({
  validationSchema: vSchema,
  mapPropsToValues: () => initValues,
  handleSubmit: () => {},
})(CCPAForm);
